// src/domains/core/context-intelligence/engine/priorityEngine.ts

import type { Insight, InsightPriority } from "../types/insights.types";
import type { RelationshipEvaluationResult } from "../types/relationships.types";

/**
 * Priority Engine
 * Calculates relevance and assigns priority to insights
 */
export const PriorityEngine = {
  /**
   * Calculate relevance score (0-1) for an insight
   * Based on: Consistency, Novelty, Impact
   *
   * @param insight
   *
   * @param relationshipResults
   *
   * @returns {number} Relevance score between 0 and 1
   *
   */
  calculateRelevance(
    insight: Insight,
    relationshipResults?: RelationshipEvaluationResult[],
  ): number {
    let relevance = 0;

    // Consistency: How stable is the relationship? (0-0.4)
    const consistency = PriorityEngine.calculateConsistency(insight, relationshipResults);
    relevance += consistency * 0.4;

    // Novelty: How new/surprising is this? (0-0.3)
    const novelty = PriorityEngine.calculateNovelty(insight);
    relevance += novelty * 0.3;

    // Impact: How strongly does it affect the user? (0-0.3)
    const impact = PriorityEngine.calculateImpact(insight);
    relevance += impact * 0.3;

    // Ensure relevance is between 0 and 1
    return Math.max(0, Math.min(1, relevance));
  },

  /**
   * Calculate consistency score
   * Based on relationship confidence and validation count
   *
   * @param insight
   *
   * @param relationshipResults
   *
   * @returns {number} Consistency score between 0 and 1
   *
   */
  calculateConsistency(
    insight: Insight,
    relationshipResults?: RelationshipEvaluationResult[],
  ): number {
    if (!relationshipResults || relationshipResults.length === 0) {
      // If no relationship data, use insight confidence as fallback
      return insight.confidence;
    }

    // Average confidence of all relationships
    const avgConfidence =
      relationshipResults.reduce((sum, result) => sum + result.confidence, 0) /
      relationshipResults.length;

    return avgConfidence;
  },

  /**
   * Calculate novelty score
   * Newer insights or those that contradict previous patterns score higher
   *
   * @param insight
   *
   * @returns {number} Novelty score between 0 and 1
   *
   */
  calculateNovelty(insight: Insight): number {
    const now = Date.now();
    const age = now - insight.generatedAt;
    const ageInDays = age / (1000 * 60 * 60 * 24);

    // Newer insights are more novel
    // After 7 days, novelty decreases
    if (ageInDays < 1) {
      return 1;
    }
    if (ageInDays < 3) {
      return 0.8;
    }
    if (ageInDays < 7) {
      return 0.6;
    }
    if (ageInDays < 14) {
      return 0.4;
    }
    return 0.2;
  },

  /**
   * Calculate impact score
   * Based on priority and category
   *
   * @param insight
   *
   * @returns {number} Impact score between 0 and 1
   *
   */
  calculateImpact(insight: Insight): number {
    // Priority-based impact
    const priorityImpact: Record<InsightPriority, number> = {
      background: 0.2,
      critical: 1,
      high: 0.8,
      low: 0.4,
      medium: 0.6,
    };

    let impact = priorityImpact[insight.priority] || 0.5;

    // Category-based adjustment
    if (insight.category === "risk") {
      impact *= 1.2; // Risk insights are more impactful
    } else if (insight.category === "opportunity") {
      impact *= 1.1; // Opportunities are also impactful
    }

    return Math.min(1, impact);
  },

  /**
   * Assign priority level based on relevance and confidence
   *
   * @param relevance
   *
   * @param confidence
   *
   * @returns {InsightPriority} Assigned priority level
   *
   */
  assignPriority(relevance: number, confidence: number): InsightPriority {
    const score = relevance * 0.6 + confidence * 0.4;

    if (score >= 0.8) {
      return "critical";
    }
    if (score >= 0.65) {
      return "high";
    }
    if (score >= 0.5) {
      return "medium";
    }
    if (score >= 0.3) {
      return "low";
    }
    return "background";
  },

  /**
   * Calculate confidence score (0-1) for an insight
   * Based on relationship strength and data quality
   *
   * @param insight
   *
   * @param relationshipResults
   *
   * @returns {number} Confidence score between 0 and 1
   *
   */
  calculateConfidence(
    insight: Insight,
    relationshipResults?: RelationshipEvaluationResult[],
  ): number {
    if (!relationshipResults || relationshipResults.length === 0) {
      return insight.confidence; // Use existing confidence as fallback
    }

    // Average confidence of relationships
    const avgConfidence =
      relationshipResults.reduce((sum, result) => sum + result.confidence, 0) /
      relationshipResults.length;

    // Adjust based on number of relationships (more relationships = higher confidence)
    const relationshipCount = relationshipResults.length;
    const countMultiplier = Math.min(1, 0.7 + relationshipCount * 0.1);

    return Math.min(1, avgConfidence * countMultiplier);
  },

  /**
   * Evaluate and update insight priority and relevance
   *
   * @param insight
   *
   * @param relationshipResults
   *
   * @returns {object} Evaluation result with relevance, confidence, and priority
   *
   */
  evaluateInsight(
    insight: Insight,
    relationshipResults?: RelationshipEvaluationResult[],
  ): {
    relevance: number;
    confidence: number;
    priority: InsightPriority;
  } {
    const relevance = PriorityEngine.calculateRelevance(insight, relationshipResults);
    const confidence = PriorityEngine.calculateConfidence(insight, relationshipResults);
    const priority = PriorityEngine.assignPriority(relevance, confidence);

    return {
      confidence,
      priority,
      relevance,
    };
  },
};
